import {useState} from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {Box, Chip, ChipType, Colors, Text} from "@bounce/shared/ui";
import {FilterPanelState} from "../../hooks/useFilterPanel";
import {LIST_HEADER_HEIGHT, listCardSx, listHeaderBandSx} from "./cardStyles";
import {FILTER_PANEL_CLASS, FilterLinesIcon, FilterPanelCollapseButton} from "./FilterPanelControls";

export type FilterOption = {
  value: string;
  label: string;
  chipVariant?: ChipType["variant_"];
};

export type FilterValues = Record<string, string[]>;

export type SidebarFilterMatchMode = "any" | "all";

export type SidebarFilter = {
  key: string;
  placeholder: string;
  options: (FilterOption | string)[];
  chipVariant?: ChipType["variant_"];
  matchMode?: {
    value: SidebarFilterMatchMode;
    onChange: (mode: SidebarFilterMatchMode) => void;
  };
};

export type ListFilter<R> = SidebarFilter & {
  getRowValues: (row: R) => string[];
};

export const matchesFilters = <R,>(row: R, filters: ListFilter<R>[], values: FilterValues) =>
  filters.every(({key, getRowValues, matchMode}) => {
    const selected = values[key] ?? [];
    if (selected.length === 0) {
      return true;
    }
    const rowValues = getRowValues(row);
    return matchMode?.value === "all"
      ? selected.every((value) => rowValues.includes(value))
      : rowValues.some((value) => selected.includes(value));
  });

export const hasActiveFilters = (values: FilterValues) => Object.values(values).some((selected) => selected.length > 0);

export const sortedByLocale = (values: string[]) => [...values].sort((a, b) => a.localeCompare(b));

export const uniqueSorted = (values: string[]) => sortedByLocale([...new Set(values)]);

export const countActiveFilters = (values: FilterValues) =>
  Object.values(values).reduce((count, selected) => count + selected.length, 0);

type Props = {
  filters: SidebarFilter[];
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  panel: FilterPanelState;
};

const normalizeOption = (option: FilterOption | string): FilterOption =>
  typeof option === "string" ? {value: option, label: option} : option;

const MATCH_MODE_OPTIONS: {mode: SidebarFilterMatchMode; label: string}[] = [
  {mode: "any", label: "Match any"},
  {mode: "all", label: "Match all"},
];

const PANEL_WIDTH = 250;
const PANEL_GAP = 2.5;
const PANEL_TITLE = "Filters";
const WIDTH_TRANSITION_DURATION = "180ms";
const OPACITY_TRANSITION_DURATION = "150ms";

const optionRowSx = {
  height: "32px",
  borderRadius: "8px",
  width: "100%",
  justifyContent: "flex-start",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
} as const;

const VARIANT_ICON_COLORS: Record<string, string> = {
  primary: Colors.bw.white,
  secondary: Colors.primary._800,
  inherit: Colors.grey._1000,
  error: Colors.red._900,
  warning: Colors.orange._900,
  success: Colors.green._900,
  info: Colors.secondary._900,
  yellow: Colors.yellow._800,
  blue: Colors.blue._800,
  pink: Colors.pink._800,
  neutral: Colors.grey._900,
};

const ClearLink = ({label = "Clear", enabled, onClear}: {label?: string; enabled: boolean; onClear: () => void}) => (
  <Box onClick={enabled ? onClear : undefined} sx={{cursor: enabled ? "pointer" : "default"}}>
    <Text
      size={12}
      variant="inter600"
      sx={{
        color: enabled ? Colors.grey._600 : Colors.grey._400,
        "&:hover": enabled ? {color: Colors.grey._900} : {},
      }}
    >
      {label}
    </Text>
  </Box>
);

const MatchModeToggle = ({
  value,
  onChange,
}: {
  value: SidebarFilterMatchMode;
  onChange: (mode: SidebarFilterMatchMode) => void;
}) => {
  const activeIndex = MATCH_MODE_OPTIONS.findIndex((o) => o.mode === value);
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        gap: "2px",
        p: "2px",
        backgroundColor: Colors.tertiary._200,
        border: `1px solid ${Colors.tertiary._300}`,
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "2px",
          left: "2px",
          width: `calc(50% - 3px)`,
          height: "calc(100% - 4px)",
          borderRadius: "6px",
          backgroundColor: Colors.bw.white,
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
          transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: `translateX(${activeIndex === 0 ? "0" : "calc(100% + 2px)"})`,
        }}
      />
      {MATCH_MODE_OPTIONS.map(({mode, label}) => {
        const isActive = mode === value;
        return (
          <Box
            key={mode}
            onClick={() => onChange(mode)}
            sx={{
              position: "relative",
              flex: 1,
              textAlign: "center",
              py: "6px",
              borderRadius: "6px",
              cursor: "pointer",
              "&:hover": {backgroundColor: isActive ? "transparent" : Colors.tertiary._100},
            }}
          >
            <Text size={12} variant="inter600" sx={{color: isActive ? Colors.grey._900 : Colors.grey._600}}>
              {label}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

export const FilterSidebar = ({filters, values, onChange, panel}: Props) => {
  const {isExpanded, panelId} = panel;
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

  const toggleCollapsed = (key: string) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <Box
      id={panelId}
      component="aside"
      aria-label={PANEL_TITLE}
      className={FILTER_PANEL_CLASS}
      sx={{
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
        width: isExpanded ? PANEL_WIDTH : 0,
        mr: isExpanded ? PANEL_GAP : 0,
        opacity: isExpanded ? 1 : 0,
        visibility: isExpanded ? "visible" : "hidden",
        transition: [
          `width ${WIDTH_TRANSITION_DURATION} ease`,
          `margin-right ${WIDTH_TRANSITION_DURATION} ease`,
          `opacity ${OPACITY_TRANSITION_DURATION} ease`,
          `visibility 0s linear ${isExpanded ? "0s" : WIDTH_TRANSITION_DURATION}`,
        ].join(", "),
        "@media (prefers-reduced-motion: reduce)": {transition: "none"},
      }}
    >
      <Box
        sx={{
          ...listCardSx,
          boxSizing: "border-box",
          width: PANEL_WIDTH,
          minWidth: PANEL_WIDTH,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            ...listHeaderBandSx,
            boxSizing: "border-box",
            height: LIST_HEADER_HEIGHT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pl: 2,
            pr: 0.75,
          }}
        >
          <Box sx={{display: "flex", alignItems: "center", gap: 1, color: Colors.grey._700}}>
            <FilterLinesIcon />
            <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
              {PANEL_TITLE}
            </Text>
          </Box>
          <FilterPanelCollapseButton panel={panel} />
        </Box>
        <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 3}}>
          {filters.map((filter, index) => {
            const selectedValues = values[filter.key] ?? [];
            const isCollapsed = collapsedKeys.has(filter.key);
            const toggleValue = (value: string) => {
              const nextSelected = selectedValues.includes(value)
                ? selectedValues.filter((selected) => selected !== value)
                : [...selectedValues, value];
              onChange({...values, [filter.key]: nextSelected});
            };
            const clearCategory = () => onChange({...values, [filter.key]: []});

            return (
              <Box
                key={filter.key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  pt: index === 0 ? 0 : 2.5,
                  borderTop: index === 0 ? "none" : `1px solid ${Colors.tertiary._200}`,
                }}
              >
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                  <Box
                    onClick={() => toggleCollapsed(filter.key)}
                    sx={{display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer"}}
                  >
                    <ExpandMoreRoundedIcon
                      sx={{
                        fontSize: 18,
                        color: Colors.grey._500,
                        transition: "transform 0.15s ease",
                        transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                      }}
                    />
                    <Text size={14} variant="inter600" sx={{color: Colors.grey._700, letterSpacing: "0.02em"}}>
                      {filter.placeholder}
                    </Text>
                  </Box>
                  <ClearLink enabled={selectedValues.length > 0} onClear={clearCategory} />
                </Box>
                {!isCollapsed && filter.matchMode && (
                  <MatchModeToggle value={filter.matchMode.value} onChange={filter.matchMode.onChange} />
                )}
                {!isCollapsed && (
                  <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                    {filter.options.map((option) => {
                      const {value, label, chipVariant} = normalizeOption(option);
                      const isSelected = selectedValues.includes(value);
                      const selectedVariant = chipVariant ?? filter.chipVariant ?? "secondary";
                      const iconColor = VARIANT_ICON_COLORS[selectedVariant] ?? Colors.grey._900;
                      return (
                        <Chip
                          key={value}
                          size_="small"
                          border
                          variant_={isSelected ? selectedVariant : "neutral"}
                          label={label}
                          icon={
                            isSelected ? (
                              <CheckRoundedIcon sx={{fontSize: 14}} />
                            ) : (
                              <AddRoundedIcon sx={{fontSize: 14}} />
                            )
                          }
                          onClick={() => toggleValue(value)}
                          sx={{
                            ...optionRowSx,
                            "& .MuiChip-label": {paddingLeft: "10px"},
                            "& .MuiChip-icon": {color: isSelected ? iconColor : Colors.grey._500},
                            ...(!isSelected && {
                              backgroundColor: "transparent",
                              "&:hover": {backgroundColor: Colors.tertiary._100, borderColor: Colors.grey._400},
                            }),
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
